import { Throbber } from '@lt/components';
import cn from 'classnames';
import type { Map as MapTypes } from 'mapbox-gl';
import React, { PureComponent, Suspense, lazy } from 'react';
import styled from 'styled-components';

import type { Coordinates } from '@lt/api/dist/types/map';
import { tilesUrlv2 } from 'src/utils/mapUtils';
import { ExpandControl } from '../../../Mapbox/components/ExpandControl';
import { LayersControl } from '../../../Mapbox/components/LayersControl';
import { ZoomControl } from '../../../Mapbox/components/ZoomControl';
import HotelMapPreloader from './HotelMapPreloader';
import HotelNoCoordPopup from './HotelNoCoordPopup';
import HotelRoulettePopup from './HotelRoulettePopup';
import type { HotelMapProps } from './types';

const Mapbox = lazy(
  () => import(/* webpackChunkName: "Mapbox" */ '../../../Mapbox'),
);

const EmptyMap = lazy(
  () =>
    import(
      /* webpackChunkName: "EmptyMap" */ '../../../Mapbox/components/EmptyMap'
    ),
);

const HotelMapMarker = lazy(
  () => import(/* webpackChunkName: "HotelMapMarker" */ './HotelMapMarker'),
);

const baseCls = 'lt-hotel-map';

interface State {
  coordinates: Coordinates | null;
  isClient: boolean;
  loaded: boolean;
  zoom: [number];
  showNoCoordsPopup: boolean;
}

class HotelMap extends PureComponent<Readonly<HotelMapProps>, State> {
  constructor(props) {
    super(props);

    this.onLoad = this.onLoad.bind(this);

    const { coordinates, isRoulette } = this.props;

    this.state = {
      coordinates: coordinates ? [coordinates.long, coordinates.lat] : null,
      zoom: [12],
      isClient: false,
      loaded: false,
      showNoCoordsPopup: isRoulette || !coordinates,
    };

    this.handleCloseNoCoordPopup = this.handleCloseNoCoordPopup.bind(this);
    this.handleIntersect = this.handleIntersect.bind(this);
    this.containerRef = React.createRef();
  }

  componentDidMount() {
    const { lazyInit } = this.props;

    if (lazyInit) {
      this.observer = new IntersectionObserver(this.handleIntersect);
      this.observer.observe(this.containerRef.current);
    } else {
      this.setState({ isClient: true });
    }
  }

  handleIntersect(entries) {
    const [entry] = entries;
    const { intersectionRatio } = entry;

    if (intersectionRatio !== 0) {
      this.setState({ isClient: true }, () => {
        this.observer.unobserve(this.containerRef.current);
      });
    }
  }

  handleCloseNoCoordPopup() {
    this.setState({ showNoCoordsPopup: false });
  }

  onLoad(map) {
    this.map = map;

    this.setState({ loaded: true });
  }

  get mapOptions() {
    const { coordinates, zoom } = this.state;
    const { height, bounds, interactive } = this.props;
    return {
      maxZoom: 18,
      attributionControl: false,
      interactive,
      containerStyle: {
        width: '100%',
        height,
      },
      zoom,
      center: coordinates || undefined,
      fitBounds: bounds,
    };
  }

  containerRef: React.RefObject<HTMLDivElement>;

  observer: any;

  map?: MapTypes;

  render() {
    const { coordinates, isClient, loaded, showNoCoordsPopup } = this.state;
    const {
      imageHref,
      isMapView,
      onExpand,
      isMobile = false,
      isWebGlSupported = true,
      height,
      location,
      isRoulette,
      className,
      showLayersControl,
      showZoomControl,
      assets,
    } = this.props;

    const showMarker = Boolean(coordinates && !isRoulette);
    const showMapControl = isMapView || !isMobile;

    return (
      <div className={cn(baseCls, className)} ref={this.containerRef}>
        {(!isClient || (isWebGlSupported && !loaded)) && (
          <StyledHotelMapWrapper isClient={isClient} height={height}>
            <HotelMapPreloader>{isClient && <Throbber />}</HotelMapPreloader>
          </StyledHotelMapWrapper>
        )}
        {loaded && showNoCoordsPopup && !showMarker && (
          <StyledNoCoordsPopupWrapper>
            {isRoulette ? (
              <HotelRoulettePopup
                isMobile={isMobile}
                location={location}
                onClose={this.handleCloseNoCoordPopup}
                assets={assets}
              />
            ) : (
              <HotelNoCoordPopup
                isMobile={isMobile}
                location={location}
                onClose={this.handleCloseNoCoordPopup}
              />
            )}
          </StyledNoCoordsPopupWrapper>
        )}
        {isClient && isWebGlSupported && (
          <Suspense fallback={null}>
            <Mapbox
              className={`${baseCls}__map`}
              onStyleLoad={this.onLoad}
              showLayersControl={showMapControl || showLayersControl}
              showZoomControl={showMapControl || showZoomControl}
              isMobile={isMobile}
              tilesUrl={tilesUrlv2}
              {...this.mapOptions}
            >
              <HotelMapMarker
                showMarker={showMarker}
                coordinates={coordinates}
                isMobile={isMobile}
                imageHref={imageHref}
              />
              {onExpand && (
                <ExpandControl
                  isExpanded={false}
                  toggleHandler={onExpand}
                  expandText="Развернуть"
                  position="bottom"
                />
              )}
            </Mapbox>
          </Suspense>
        )}
        {isClient && !isWebGlSupported && (
          <div
            className={cn(`${baseCls}__stub`, {
              [`${baseCls}__stub--bg`]: !isMobile,
            })}
          >
            <Suspense fallback={null}>
              <EmptyMap isMobile={isMobile} />
            </Suspense>
            {!isMobile && (
              <>
                <LayersControl isMobile={false} />
                <ZoomControl isMobile={false} />
              </>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default HotelMap;

const StyledHotelMapWrapper = styled.div<{
  isClient: boolean;
  height: string | number;
}>`
  position: ${({ isClient }) => (isClient ? 'absolute' : 'relative')};
  z-index: 9;
  width: 100%;
  height: ${({ height }) =>
    Number.isNaN(Number(height)) ? height : `${height}px`};
`;

const StyledNoCoordsPopupWrapper = styled.div`
  position: absolute;
  z-index: 11;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;
